/**
 * Carson OS 外壳。
 *
 * 结构上留位、内容上克制（PLAN §1）：三个页位现在都是空的，M1 先把「学业」填上——
 * 那是让 Carson 每天愿意打开这个 App 的钩子。壳收的 items 只有一份，
 * AppShell 在窄屏渲染成底部 TabBar、宽屏渲染成左侧 SideNav，业务页两边共用同一个组件。
 *
 * ⚠️ 外壳必须能离线打开：本文件到「已解锁 / 未解锁」为止都不依赖后端。
 * 断网时看到的是锁屏而不是白屏——白屏本身会被当成故障。
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useOnline } from '@pwaship/react'
import {
  AppShell,
  EmptyState,
  NavBar,
  OfflineBar,
  Screen,
} from '@pwaship/ui'

import SelfTestScreen from './screens/SelfTest'
import Academics from './screens/Academics'
import Today from './screens/Today'
import Money from './screens/Money'
import Trail from './screens/Trail'
import { api, session, type NavTab } from './lib/api'
import { IconToday, IconGoal, IconMoney, IconTrail, IconAdmin } from './components/Icons'

export interface AppProps {
  path: string
  navigate(path: string): void
}

const ICON_FOR: Record<string, (p: { active?: boolean }) => ReactNode> = {
  today: IconToday, academics: IconGoal, consumption: IconMoney, timeline: IconTrail, admin: IconAdmin,
}

export default function App({ path, navigate }: AppProps) {
  // 能力自检场留在隐藏路由，别删（AGENTS.md 的要求，真机上只能靠它看能力是否活着）
  if (path.startsWith('/__selftest')) return <SelfTestScreen path={path} navigate={navigate} />

  return <Shell />
}

function Shell() {
  const online = useOnline()
  const [role, setRole] = useState<'carson' | 'neo' | null>(session.role)
  const [tabs, setTabs] = useState<NavTab[]>([])
  // 每次打开都落在「今天」：那是他要动手的地方（董事长 2026-08-25）。
  // 刻意不记住上次停留的页签 —— 记住的话，某天点去「目标」看一眼，
  // 之后每次打开都停在那儿，日历和计时反而要多点一下才看得到。
  const [active, setActive] = useState('today')

  const loadNav = useCallback(async () => {
    const r = await api.get<{ role: 'carson' | 'neo'; tabs: NavTab[] }>('/api/nav')
    if (r.ok) {
      setTabs(r.data.tabs)
      setActive((cur) => (r.data.tabs.some((t) => t.key === cur) ? cur : (r.data.tabs[0]?.key ?? cur)))
    } else if (r.kind === 'unauthorized') {
      setRole(null)
    }
  }, [])

  useEffect(() => { if (role) void loadNav() }, [role, loadNav])

  if (!role) return <Lock online={online} onUnlocked={setRole} />

  const items = tabs.map((t) => {
    const I = ICON_FOR[t.key] ?? IconTrail
    return { key: t.key, label: t.label, icon: <I active={active === t.key} /> }
  })
  const current = tabs.find((t) => t.key === active)

  // 「今天」页自带日期 + 每日一句做页头，再叠一条只写着「今天」的标题栏纯属占地方
  return (
    <AppShell
      items={items}
      active={active}
      onChange={setActive}
      brand={<strong style={{ fontSize: 17 }}>Carson Claw</strong>}
      header={active === 'today' ? undefined : <NavBar title={current?.label ?? 'Carson Claw'} />}
    >
      {!online && <OfflineBar />}
      {active === 'today' ? <Today />
        : active === 'academics' ? <Academics role={role} />
        : active === 'consumption' ? <Money />
        : active === 'timeline' ? <Trail />
        : <ComingSoon label={current?.label ?? ''} tabKey={active} />}
    </AppShell>
  )
}

/** 页位占位。M1 起逐个换成真页面，壳不需要跟着改。 */
function ComingSoon({ label, tabKey }: { label: string; tabKey: string }) {
  const next: Record<string, string> = {
    academics: 'M1 会把 AP / SAT 的目标、进度和考期倒数放在这里',
    consumption: 'M2 会把本月消费进度、分类和建议放在这里',
    timeline: 'M4 会把学业、消费、阅读的痕迹合成一条时间线',
    admin: 'M4 会把推送内容和完成度放在这里',
  }
  return (
    <EmptyState
      title={`${label}还没做`}
      description={next[tabKey] ?? '这个页位留着，后面会填上'}
    />
  )
}

function Lock({ online, onUnlocked }: { online: boolean; onUnlocked(r: 'carson' | 'neo'): void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // 董事长 2026-08-25：默认显示明文 —— 用中文输入法打密码看不见自己输了什么很难受
  const [reveal, setReveal] = useState(true)
  // 支持 -webkit-text-security 才敢用普通文本框，否则密码会明文露出来
  const maskable = useMemo(
    () => typeof CSS !== 'undefined' && CSS.supports?.('-webkit-text-security', 'disc'),
    [],
  )
  // 输入法组字期间不要动输入内容，否则候选词会被打断
  const composing = useRef(false)
  const strip = (v: string) => v.replace(/[\s\u3000\u200B-\u200D\uFEFF]+/g, '')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password || busy) return
    setBusy(true)
    setError(null)
    const r = await api.unlock(strip(password))
    setBusy(false)
    if (r.ok) {
      setPassword('')
      onUnlocked(r.data.role)
    } else {
      setError(r.message)
      setPassword('')
    }
  }

  return (
    <Screen>
      {!online && <OfflineBar />}
      <form onSubmit={submit} className="safe-b" style={{
        display: 'grid', gap: 14, padding: '0 24px', width: '100%', maxWidth: 360,
        margin: '14vh auto 0',
      }}>
        <div className="rise rise-1" style={{ display: 'grid', justifyItems: 'center', gap: 12, marginBottom: 6 }}>
          <img src="/frog.png" alt="" width={200} height={162}
            style={{ display: 'block', width: 200, height: 'auto' }} />
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900, letterSpacing: '-.03em' }}>Carson Claw</h1>
          <p style={{ margin: 0, color: 'var(--ps-text3)', fontSize: 13.5 }}>输入密码即可进入，不需要用户名</p>
        </div>

        <div className="rise rise-2" style={{ position: 'relative', display: 'grid' }}>
          <input
            type={maskable ? 'text' : 'password'}
            value={password}
            autoFocus
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            onCompositionStart={() => { composing.current = true }}
            onCompositionEnd={(e) => { composing.current = false; setPassword(strip(e.currentTarget.value)) }}
            onChange={(e) => setPassword(composing.current ? e.target.value : strip(e.target.value))}
            aria-label="访问密码"
            style={{
              padding: '15px 62px 15px 18px', fontSize: 17, borderRadius: 999, fontFamily: 'inherit',
              border: '1px solid var(--ps-line)', background: 'var(--ps-surface)', color: 'var(--ps-text)',
              ...(maskable && !reveal ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : {}),
            }}
          />
          <button type="button" onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? '隐藏密码' : '显示密码'}
            style={{
              position: 'absolute', right: 8, top: 0, bottom: 0, width: 48,
              display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0,
              background: 'transparent', border: 'none', color: 'var(--ps-text3)', fontSize: 12.5,
            }}>{reveal ? '隐藏' : '显示'}</button>
        </div>

        {error && <p role="alert" className="rise" style={{ margin: 0, color: 'var(--cal-missed-fg)', fontSize: 13, textAlign: 'center' }}>{error}</p>}

        <button type="submit" disabled={!password || busy} className="rise rise-3" style={{
          minHeight: 54, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: 'var(--ps-accent)', color: 'var(--ps-on-accent)',
          fontSize: 16.5, fontWeight: 800, letterSpacing: '.02em',
          boxShadow: 'var(--ps-shadow-accent)',
        }}>{busy ? '正在解锁…' : '解锁'}</button>
      </form>
    </Screen>
  )
}
